import styled from "@emotion/styled/macro";
import { css } from "@emotion/react";
import { ReactElement } from "react";
import { LoadingIndicator, tokens } from "@sunrun/experience-ui-components";
import {
  contentPadding,
  contentPaddingMobile,
  maxWidth,
} from "../atoms/GlobalStyles"; // eslint-disable-line import/no-restricted-paths
import shareIcon from "../assets/images/share-icon.svg";
import refreshIcon from "../assets/images/refresh.svg";
import plusIcon from "../assets/images/plus.svg";
import { HeaderContentLayoutContainer } from "components/organisms/HeaderContentLayoutContainer";
import { PositionedHeader } from "components/organisms/PositionedHeader";

type HeaderButton = {
  type: "primary" | "secondary" | "icon" | "iconAndText" | "primaryWithIcon";
  text?: string;
  altText?: string;
  icon?: "share" | "refresh" | "plus";
  onClick: () => void;
  disabled?: boolean;
  isLoading?: boolean;
  borderless?: boolean;
  LRPadding?: boolean;
  dataTestId?: string;
};

type HeaderContentLayoutProps = {
  content: ReactElement;
  title?: string;
  description?: ReactElement | string;
  kicker?: string;
  buttons?: HeaderButton[];
  smallPadding?: boolean;
  borderless?: boolean;
  refSetter?: (node: HTMLDivElement | null) => void;
  showBottomButtons?: boolean;
};

const HeaderContentLayout = ({
  content,
  title,
  description,
  kicker,
  buttons,
  smallPadding,
  refSetter,
  showBottomButtons,
}: HeaderContentLayoutProps) => {
  return (
    <HeaderContentLayoutContainer>
      <HeaderActionArea
        smallPadding={smallPadding}
        buttons={buttons}
        showBottomButtons={showBottomButtons}
      />
      <StyledPositionedHeader
        className={smallPadding ? "smallPadding" : ""}
        title={title}
        description={description}
        kicker={kicker}
      />
      <PageContent
        ref={refSetter}
        className={smallPadding ? "smallPadding" : ""}
        showBottomButtons
      >
        {content}
      </PageContent>
      {showBottomButtons && (
        <StickyBottomActionArea>
          <HeaderActionArea smallPadding={smallPadding} buttons={buttons} />
        </StickyBottomActionArea>
      )}
    </HeaderContentLayoutContainer>
  );
};

const StdButton = css`
  font-size: 16px;
  border-radius: 28px;
  padding: 1rem 2rem;
  border: 1px solid ${tokens.BRAND_HERO_BLUE};
  background: none;
  color: ${tokens.BRAND_HERO_BLUE};

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  cursor: pointer;

  &.borderless {
    border-width: 0px;
  }

  &:hover {
    opacity: 0.6;
  }

  &[disabled],
  &[disabled]:hover {
    background: #bbbcc5;
    border: 1px solid #bbbcc5;
    opacity: 1;
    cursor: default;
  }

  > img {
    display: block;
  }
`;

const IconActionButton = styled.button`
  ${StdButton}
  padding: 0px;

  @media (max-width: ${maxWidth}px) {
    border-radius: 50%;
    min-width: 32px;

    span {
      display: none;
    }

    img {
      margin: 0;
    }
  }
`;

const SecondaryActionButton = styled.button`
  ${StdButton}

  @media (max-width: ${maxWidth}px) {
    font-size: 14px;
    padding: 1rem 1rem;
  }
`;

const PrimaryActionButton = styled.button`
  ${StdButton}
  color: ${tokens.WHITE};
  background: ${tokens.BRAND_HERO_BLUE};
`;

const PrimaryWithIconActionButton = styled.button`
  ${StdButton}
  color: ${tokens.WHITE};
  background: ${tokens.BRAND_HERO_BLUE};
  padding: 12px 24px;
  line-height: 20px;
  font-weight: 500;
  white-space: nowrap;

  img {
    width: 25px;
    height: 25px;
    margin-right: 4px;
  }

  @media (max-width: 1024px) {
    padding: 8px;
    border-radius: 50%;
    min-width: 30px;

    span {
      display: none;
    }

    img {
      margin: 4px;
    }
  }
`;

const HeaderActionArea: React.FC<{
  buttons?: HeaderButton[];
  smallPadding?: boolean;
  showBottomButtons?: boolean;
}> = ({ buttons, smallPadding, showBottomButtons }) => {
  return (
    <HeaderActionSection
      className={smallPadding ? "smallPadding" : ""}
      showBottomButtons={showBottomButtons}
    >
      {buttons &&
        buttons.map((button, index) => {
          let iconSource;
          let iconText;
          switch (button.icon) {
            case "share":
              iconSource = shareIcon;
              iconText = button.altText;
              break;
            case "refresh":
              iconSource = refreshIcon;
              iconText = button.altText;
              break;
            case "plus":
              iconSource = plusIcon;
              iconText = button.altText;
              break;
          }
          switch (button.type) {
            case "primary":
              return (
                <PrimaryActionButton
                  key={index}
                  disabled={button.disabled || button.isLoading}
                  onClick={() => button.onClick()}
                  data-testid={button.dataTestId}
                >
                  {button.isLoading ? (
                    <LoadingIndicator
                      color="white"
                      height={20}
                      width={20}
                      showLoadingMessage={false}
                    />
                  ) : (
                    button.text
                  )}
                </PrimaryActionButton>
              );
            case "secondary":
              return (
                <SecondaryActionButton
                  key={index}
                  disabled={button.disabled}
                  onClick={() => button.onClick()}
                  data-testid={button.dataTestId}
                >
                  {button.text}
                </SecondaryActionButton>
              );
            case "icon":
              return (
                <IconActionButton
                  data-testid={index + "-icon-action-button"}
                  key={index}
                  disabled={button.disabled}
                  onClick={() => button.onClick()}
                  className={button.borderless ? "borderless" : ""}
                >
                  <img alt={iconText} src={iconSource}></img>
                  {button.text && <span>{button.text}</span>}
                </IconActionButton>
              );
            case "primaryWithIcon":
              return (
                <PrimaryWithIconActionButton
                  key={index}
                  disabled={button.disabled}
                  onClick={() => button.onClick()}
                  data-testid={button.dataTestId}
                >
                  <img alt={iconText} src={iconSource}></img>
                  {button.text && <span>{button.text}</span>}
                </PrimaryWithIconActionButton>
              );
            default:
              return <div>Button {button.text} has an invalid type</div>;
          }
        })}
    </HeaderActionSection>
  );
};

const HeaderActionSection = styled.div<{ showBottomButtons?: boolean }>`
  width: 100%;
  box-sizing: border-box;
  display: flex;
  grid-row: 1;
  align-self: center;
  gap: 16px;
  padding-right: ${contentPadding}px;
  align-items: center;
  justify-content: flex-end;
  ${({ showBottomButtons }) =>
    showBottomButtons &&
    css`
      display: none;
      @media (max-width: 1024px) {
        display: flex;
      }
    `}

  .disabled {
    opacity: 0.6;
  }

  @media (max-width: 1024px) {
    padding-right: ${contentPaddingMobile}px;
    position: relative;
    top: 0;
    gap: 8px;
  }

  @media (max-width: ${maxWidth}px) {
    padding: 16px ${contentPaddingMobile}px 8px;
    background-color: transparent;
    position: relative;
    margin-top: 0;
    grid-row: page-top;
    justify-content: flex-start;
  }
`;

const PageContent = styled.div<{ showBottomButtons?: boolean }>`
  grid-column: main / span 2;
  grid-row: header-end;
  overflow: auto;
  padding-left: ${contentPadding}px;
  padding-right: ${contentPadding}px;
  ${({ showBottomButtons }) =>
    showBottomButtons &&
    css`
      @media (min-width: 1025px) {
        margin-bottom: 116px;
      }
    `}
  &.smallPadding {
    padding-left: ${contentPaddingMobile}px;
    padding-right: ${contentPaddingMobile}px;
  }

  @media (max-width: ${maxWidth}px) {
    padding-left: ${contentPaddingMobile}px;
    padding-right: ${contentPaddingMobile}px;
    grid-row: content-end;
  }
`;

const StyledPositionedHeader = styled(PositionedHeader)`
  @media (max-width: ${maxWidth}px) {
    grid-row: header-end;
    padding-top: 0;
  }
`;

const StickyBottomActionArea = styled(HeaderActionSection)`
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  background: #fffcf5;
  z-index: 10;
  padding: 32px 0;
  display: flex;
  justify-content: flex-end;
  display: none;

  @media (min-width: 1025px) {
    display: flex;
  }
`;

export type { HeaderButton };
export { HeaderContentLayout };
